<template>
  <div class="intervenant-budget-content">
    <!-- Header -->
    <h2 class="intervenant-title">
      <i class="pi pi-users"></i>
      {{ $t('reports.intervenantBudgetReport') }}
    </h2>

    <!-- Search Box -->
    <div class="search-container">
      <span class="p-input-icon-left">
        <i class="pi pi-search"></i>
        <input 
          v-model="searchQuery"
          type="text" 
          class="search-input"
          :placeholder="$t('common.search')"
        />
      </span>
      <span v-if="searchQuery" class="search-count">
        {{ filteredIntervenants.length }} {{ $t('common.results') }}
      </span>
    </div>

    <!-- Intervenants Grid -->
    <div class="intervenants-grid">
      <IntervenantBudgetCard
        v-for="intervenant in paginatedIntervenants" 
        :key="intervenant.intervenantId"
        :intervenant="intervenant"
      />

    </div>

    <!-- Pagination Controls -->
    <PaginationControls
      v-model:currentPage="currentPage"
      :pageSize="pageSize"
      :totalItems="filteredIntervenants.length"
      :itemsLabel="$t('reports.intervenants.title').toLowerCase()"
    />
  </div>
</template>

<script setup lang="ts">
import IntervenantBudgetCard from './IntervenantBudgetCard.vue';
import PaginationControls from '@/components/common/PaginationControls.vue';
// @ts-ignore
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

// Access i18n composable functions
const { t } = useI18n();

interface IntervenantProps {
  intervenants?: any[];
  loading?: boolean;
}

const props = defineProps<IntervenantProps>();

// Search query
const searchQuery = ref('');

// Pagination state
const currentPage = ref<number>(1);
const pageSize = ref<number>(10);

// Reset pagination when search query changes
watch(searchQuery, () => {
  currentPage.value = 1;
});

// Computed properties
const filteredIntervenants = computed(() => {
  const query = searchQuery.value.toLowerCase().trim();
  
  if (!query) {
    return [...(props.intervenants || [])];
  }
  
  return (props.intervenants || []).filter((intervenant: any) => {
    return (
      intervenant.intervenantName?.toLowerCase().includes(query) ||
      intervenant.role?.toLowerCase().includes(query) ||
      intervenant.company?.toLowerCase().includes(query) ||
      intervenant.email?.toLowerCase().includes(query) ||
      intervenant.phone?.toLowerCase().includes(query)
    );
  });
});

const paginatedIntervenants = computed(() => {
  const start = (currentPage.value - 1) * pageSize.value;
  const end = start + pageSize.value;
  return filteredIntervenants.value.slice(start, end);
});
</script>

<style scoped>
/* Intervenant Budget Content */
.intervenant-budget-content {
  padding: var(--space-4);
}

/* Header */
.intervenant-title {
  display: flex;
  align-items: center;
  gap: var(--space-3);
  margin: 0 0 var(--space-4) 0;
  font-size: var(--font-size-xl);
  font-weight: var(--font-weight-semibold);
  color: var(--neutral-800);
}

.intervenant-title i {
  color: var(--primary);
  font-size: var(--font-size-xl);
}

/* Search Container */
.search-container {
  display: flex;
  align-items: center;
  gap: var(--space-3);
  margin-bottom: var(--space-6);
}

.p-input-icon-left {
  flex: 1;
  position: relative;
  display: flex;
  align-items: center;
}

.p-input-icon-left i {
  position: absolute;
  left: var(--space-3);
  color: var(--neutral-500);
  z-index: 1;
}

.search-input {
  width: 100%;
  padding: var(--space-3) var(--space-3) var(--space-3) calc(var(--space-8) + var(--space-2));
  border: 1px solid var(--neutral-300);
  border-radius: var(--border-radius-md);
  font-size: var(--font-size-base);
  transition: border-color 0.2s, box-shadow 0.2s;
}

.search-input:focus {
  outline: none;
  border-color: var(--primary);
  box-shadow: 0 0 0 3px rgba(var(--primary-rgb), 0.1);
}

.search-input::placeholder {
  color: var(--neutral-400);
}

.search-count {
  font-size: var(--font-size-sm);
  color: var(--neutral-600);
  white-space: nowrap;
  font-weight: var(--font-weight-medium);
}

/* Intervenants Grid */
.intervenants-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: var(--space-6);
}

/* Responsive Design */
@media (max-width: 768px) {
  .intervenant-budget-content {
    padding: var(--space-3);
  }

  .intervenant-title {
    font-size: var(--font-size-lg);
  }

  .intervenants-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 480px) {
  .intervenant-budget-content {
    padding: var(--space-2);
  }

  .intervenant-title {
    font-size: var(--font-size-base);
  }
}
</style>
